import React, { useEffect, useState } from 'react';
import AccountSettingsTab from '../../../../layouts/accountSettingsLayout/accountSettingsTab';
import {
  Button,
  Checkbox,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useToasts,
  MultiSelect,
  CheckItem,
  Text,
} from '@bloobirds-it/flamingo-ui';
import {
  useUserSettings,
  useUserSettingsReload,
} from '../../../../components/userPermissions/hooks';
import styles from './twilio.module.css';
import { useEntity } from '../../../../hooks';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import AddEditPhoneModal from './addEditPhoneModal/addEditPhoneModal';
import { useAddPhoneNumberModal } from '../../../../hooks/useAddPhoneNumberModal';
import { CallsApi } from '../../../../misc/api/calls';
import useSWR from 'swr';
import { RestApi } from '../../../../misc/api/rest';
import {
  AccountSettingsTabContent,
  AccountSettingsTabHeader,
  AccountSettingsTabHeaderLeft,
  AccountSettingsTabHeaderRight,
  AccountSettingsTableContainer,
  AccountSettingsTabSubtitle,
  AccountSettingsTabTitle,
} from '../../../../layouts/accountSettingsLayout/accountSettingsTab/accountSettingsTab';
import {
  AccountSettingsSection,
  AccountSettingsSectionContent,
} from '../../../../layouts/accountSettingsLayout/accountSettingsSection/accountSettingsSection';
import { ConfirmDeleteTwilioModal } from './confirmDeleteTwilioModal/confirmDeleteTwilioModal';
import { SearchLogs } from '../../../../../assets/svg';

const PhoneNumberRow = ({ phone, handleEdit, userList }) => {
  const { createToast } = useToasts();
  const { data, mutate } = useSWR(`phoneNumbers/${phone.id}/users`, () =>
    RestApi.get({ path: `phoneNumbers/${phone.id}/users` }),
  );

  const handleAssignUsersToPhone = users => {
    CallsApi.patch({
      path: `twilio/sync/assignNumber/${phone.id}`,
      body: {
        users_id: users,
      },
    })
      .then(() => mutate())
      .catch(() =>
        createToast({ message: 'There was an error updating the phone number', type: 'error' }),
      );
  };
  const [selectedUsers, setSelectedUsers] = useState(data?._embedded?.users.map(user => user.id));

  useEffect(() => {
    if (data) {
      setSelectedUsers(data?._embedded?.users.map(user => user.id));
    }
  }, [data]);

  return (
    <TableRow>
      <TableCell>{phone.phoneNumber}</TableCell>
      <TableCell>
        <MultiSelect
          placeholder="Bloobirds users"
          size="small"
          width={200}
          onClose={handleAssignUsersToPhone}
          value={selectedUsers}
          defaultValue={selectedUsers}
          onChange={value => setSelectedUsers(value)}
        >
          {userList &&
            userList.map(user => (
              <CheckItem dataTest={user?.value} value={user?.id} key={user?.id}>
                {user?.value}
              </CheckItem>
            ))}
        </MultiSelect>
      </TableCell>
      <TableCell>{phone.location}</TableCell>
      <TableCell>{phone.type === 'VERIFIED_NUMBER' ? 'Yes' : 'No'}</TableCell>
      <TableCell>{phone.sid}</TableCell>
      <TableCell>{phone.phoneByDefault ? 'Yes' : 'No'}</TableCell>
      <TableCell>
        <div className={styles.__edit__button} onClick={handleEdit}>
          <Icon name="edit" size={19} />
        </div>
      </TableCell>
    </TableRow>
  );
};

const TwilioConfig = ({ setModalVisible }) => {
  const settings = useUserSettings();
  const reloadUserSettings = useUserSettingsReload();
  const { createToast } = useToasts();
  const methods = useForm({ defaultValues: { ...settings.account } });
  const twilioNumbers = useEntity('phoneNumbers')
    ?.all()
    .filter(number => number.type === 'TWILIO_NUMBER' || number.type === 'VERIFIED_NUMBER');
  const { openCreateAddPhoneModal, openEditAddPhoneModal, open } = useAddPhoneNumberModal();
  const bobjectGlobalPicklist = useEntity('bobjectGlobalPicklists');
  const userGlobalPicklist = bobjectGlobalPicklist?.findByLogicRole('USER');
  const bobjectPicklistFieldValues = useEntity('bobjectPicklistFieldValues');
  const dialerTypes = useEntity('dialerTypes');
  const [confirmModalOpen, setConfirmModalOpen] = useState();

  const userList = bobjectPicklistFieldValues
    ?.filterBy('bobjectGlobalPicklist', userGlobalPicklist?.id)
    ?.filter(x => x.enabled);

  const handleDeleteTwilio = () => {
    const newDialerTypes = settings.account?.dialerTypes?.map(
      type =>
        `dialerTypes/${
          dialerTypes.find(t => t.enumName === type && t.enumName !== 'BLOOBIRDS_DIALER')?.id
        }`,
    );
    RestApi.patch({
      entity: 'accounts',
      id: settings.account?.id,
      body: {
        twilioApplicationSid: null,
        twilioAccountSid: null,
        twilioAuthToken: null,
        dialerTypes: newDialerTypes,
      },
    }).then(() => {
      createToast({ message: 'Twilio integration deleted succesfully!', type: 'success' });
      reloadUserSettings();
    });
  };

  const handleToggleRecording = () => {
    RestApi.patch({
      entity: 'accounts',
      id: settings.account?.id,
      body: methods.getValues(),
    }).then(() => {
      createToast({ message: 'Twilio settings updated!', type: 'success' });
      reloadUserSettings();
      setModalVisible(false);
    });
  };

  return (
    <>
      <AccountSettingsTab>
        <AccountSettingsTabHeader>
          <AccountSettingsTabHeaderLeft>
            <AccountSettingsTabTitle icon="person">Twilio account</AccountSettingsTabTitle>
            <AccountSettingsTabSubtitle>
              See how to create an set up a Twilio account{' '}
              <a
                className={styles._link__button}
                href="https://support.bloobirds.com/hc/en-us/articles/360011451399-Set-up-your-Twilio-account"
                target="_blank"
                rel="noreferrer"
              >
                here
              </a>
            </AccountSettingsTabSubtitle>
          </AccountSettingsTabHeaderLeft>
        </AccountSettingsTabHeader>
        <AccountSettingsTabContent>
          <FormProvider {...methods}>
            <AccountSettingsSection>
              <AccountSettingsSectionContent>
                <div className={styles._config__account}>
                  <Text className={styles._config__account_name} size="m" color="softPeanut">
                    SID: {settings.account?.twilioAccountSid}
                  </Text>
                  <Button variant="clear" onClick={() => setModalVisible(true)}>
                    EDIT
                  </Button>
                  <Button
                    variant="tertiary"
                    color="tomato"
                    onClick={() => setConfirmModalOpen(true)}
                  >
                    Delete
                  </Button>
                </div>
                <Controller
                  name="enableCallRecording"
                  onChangeName="onClick"
                  render={({ onChange, value }) => (
                    <Checkbox
                      checked={value}
                      onClick={v => {
                        onChange(v);
                        handleToggleRecording();
                      }}
                    >
                      Enable call recording
                    </Checkbox>
                  )}
                />
              </AccountSettingsSectionContent>
            </AccountSettingsSection>
          </FormProvider>
        </AccountSettingsTabContent>
      </AccountSettingsTab>
      <AccountSettingsTab>
        <AccountSettingsTabHeader>
          <AccountSettingsTabHeaderLeft>
            <AccountSettingsTabTitle icon="phone">Connected numbers</AccountSettingsTabTitle>
            <AccountSettingsTabSubtitle>
              Add your twilio numbers and assign them to your Bloobirds users to use them in our
              dialer.
            </AccountSettingsTabSubtitle>
          </AccountSettingsTabHeaderLeft>
          <AccountSettingsTabHeaderRight>
            <Button iconLeft="plus" onClick={openCreateAddPhoneModal}>
              New phone number
            </Button>
          </AccountSettingsTabHeaderRight>
        </AccountSettingsTabHeader>
        <AccountSettingsTabContent>
          <AccountSettingsTableContainer>
            {twilioNumbers?.length > 0 ? (
              <Table className={styles.__table_row__container}>
                <TableHead>
                  <TableCell>Phone numbers</TableCell>
                  <TableCell>Bloobirds users</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Verified caller ID</TableCell>
                  <TableCell>SID</TableCell>
                  <TableCell>Phone by default</TableCell>
                </TableHead>
                <TableBody>
                  {twilioNumbers?.map(phone => (
                    <PhoneNumberRow
                      key={phone.id}
                      phone={phone}
                      handleEdit={() => openEditAddPhoneModal(phone)}
                      userList={userList}
                    />
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className={styles._phone_numbers_empty}>
                <SearchLogs className={styles._phone_numbers_empty_img} />
                <Text size="xl" weight="bold" align="center" color="softPeanut">
                  No phone numbers could be found
                </Text>
              </div>
            )}
          </AccountSettingsTableContainer>
        </AccountSettingsTabContent>
        {open && <AddEditPhoneModal />}
      </AccountSettingsTab>
      <ConfirmDeleteTwilioModal
        handleDeleteTwilio={handleDeleteTwilio}
        confirmModalOpen={confirmModalOpen}
        setConfirmModalOpen={setConfirmModalOpen}
      />
    </>
  );
};

export default TwilioConfig;
